// src/pages/markets/board/api/useNewArrivals.ts
//
// Which ids appeared since the reader last looked. Four things are explicitly
// NOT arrivals: the first list (a cold load would light up everything), a page
// append (the reader asked for those), a filter change (different question),
// and a kept-previous response (data that belongs to the old question).
//
// Takes rank ids, not events: events resolve one at a time through the batcher
// and out of rank order, so a half-filled event array reports its own fill as
// arrivals. Rank rows land in one response and are complete.
//
// Freshness expires on its own after NEW_TTL_MS — the cue means "look at this
// now", and an unattended tab must not accumulate a wall of them.

import { useCallback, useEffect, useRef, useState } from 'react';

const EMPTY: ReadonlyMap<string, number> = new Map();
const NEW_TTL_MS = 90_000;
const SWEEP_MS = 5_000;

export interface NewArrivals {
    isNew: (id: string) => boolean;
    markSeen: (id: string) => void;
    count: number;
}

export interface NewArrivalsOptions {
    // False while the query is loading or while it is serving placeholder data
    // from the previous filter set. Seeding on either one is what makes the
    // real response look brand new.
    ready?: boolean;
    // Pages currently held. Any increase is a page append, absorbed silently.
    // Omit it and the prefix rule below covers the same case, slightly less
    // precisely.
    pageCount?: number;
    ttlMs?: number;
}

export function useNewArrivals(
    ids: readonly string[],
    resetKey: string,
    { ready = true, pageCount, ttlMs = NEW_TTL_MS }: NewArrivalsOptions = {},
): NewArrivals {
    const knownRef = useRef<Set<string>>(new Set());
    const vettedRef = useRef(0);
    const pagesRef = useRef(pageCount ?? 0);
    const seededRef = useRef(false);
    const keyRef = useRef(resetKey);
    const [fresh, setFresh] = useState<ReadonlyMap<string, number>>(EMPTY);

    // A filter change is not an arrival — it is a different question.
    useEffect(() => {
        if (keyRef.current === resetKey) return;
        keyRef.current = resetKey;
        seededRef.current = false;
        knownRef.current = new Set();
        vettedRef.current = 0;
        pagesRef.current = 0;
        setFresh(EMPTY);
    }, [resetKey]);

    useEffect(() => {
        if (!ready) return;

        const absorb = () => {
            for (const id of ids) knownRef.current.add(id);
            vettedRef.current = Math.max(vettedRef.current, ids.length);
        };

        // Seed on the first settled response, empty or not.
        if (!seededRef.current) {
            seededRef.current = true;
            pagesRef.current = pageCount ?? pagesRef.current;
            absorb();
            return;
        }

        // A page append is the reader's own doing.
        if (pageCount !== undefined && pageCount > pagesRef.current) {
            pagesRef.current = pageCount;
            absorb();
            return;
        }

        // Backstop when pageCount is not wired: only the prefix already vetted
        // can hold something genuinely new. An event inserted at the top grows
        // the list but lands inside that prefix; a tail append never does.
        const scope = pageCount === undefined ? ids.slice(0, vettedRef.current) : ids;
        const arrived = scope.filter(id => !knownRef.current.has(id));

        absorb();
        if (arrived.length === 0) return;

        const at = Date.now();
        setFresh(prev => {
            const next = new Map(prev);
            for (const id of arrived) next.set(id, at);
            return next;
        });
    }, [ids, ready, pageCount]);

    // Self-expiry, so the cue clears even for cards the reader never hovers.
    useEffect(() => {
        if (fresh.size === 0) return;
        const timer = window.setInterval(() => {
            const cutoff = Date.now() - ttlMs;
            setFresh(prev => {
                let changed = false;
                const next = new Map(prev);
                for (const [id, at] of next) {
                    if (at < cutoff) {
                        next.delete(id);
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        }, SWEEP_MS);
        return () => window.clearInterval(timer);
    }, [fresh.size, ttlMs]);

    const markSeen = useCallback((id: string) => {
        setFresh(prev => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const isNew = useCallback((id: string) => fresh.has(id), [fresh]);

    return { isNew, markSeen, count: fresh.size };
}